<template>
  <div id="agora-call">
    <!-- Overlay -->
    <div
      v-if="expanded"
      class="modal-overlay"
      @click="callStatus !== 'calling' && (expanded = false)"
    />
    <!-- Gelen/giden arama çalarken dışarı yanlışlıkla tıklanınca küçülmesin: küçük balonda kabul/reddet yok -->

    <b-overlay
      :show="loading"
      variant="transparent"
      rounded="sm"
    >
      <!-- Tam ekran "Sesi Aç" katmanı kaldırıldı: ilk aramada 1-2 sn kabul/reddet butonlarının üstünü kapatıp
           tıklanamaz yapıyordu. Ses kilidi artık App.vue'da kullanıcının sitedeki ilk dokunuşunda sessizce açılıyor. -->

      <div
        v-if="expanded"
        ref="callCard"
        class="expanded"
        :class="'status-' + callStatus"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <div
          class="call-glow"
          aria-hidden="true"
        />
        <div class="window-actions">
          <button
            type="button"
            class="minus"
            :title="$t('Minimize')"
            :aria-label="$t('Minimize')"
            @click="expanded = false"
          >
            <feather-icon
              icon="MinimizeIcon"
              size="16"
            />
          </button>
        </div>

        <div
          v-if="agoraCallUser"
          class="call-information"
        >
          <div class="call-avatar-wrap">
            <span
              v-if="callStatus === 'calling'"
              class="call-ring ring-1"
            />
            <span
              v-if="callStatus === 'calling'"
              class="call-ring ring-2"
            />
            <b-avatar
              size="104"
              :src="agoraCallUser.profile.image"
              class="call-avatar"
            />
          </div>
          <span class="username">
            {{ agoraCallUser.userName }}
          </span>
          <span class="status">
            <span
              class="status-dot"
              :class="callStatus"
            />
            {{ message }}
          </span>
        </div>

        <div class="agora-call-buttons">
          <button
            v-if="callStatus === 'talking'"
            type="button"
            class="call-btn neutral"
            :class="{ muted: localeMuted }"
            :title="localeMuted ? $t('Unmute') : $t('Mute')"
            :aria-label="localeMuted ? $t('Unmute') : $t('Mute')"
            @click="localeMuteToggle"
          >
            <feather-icon
              :icon="localeMuted ? 'MicOffIcon' : 'MicIcon'"
              size="24"
            />
          </button>

          <button
            v-if="agoraCallType === 'incoming' && callStatus !== 'talking'"
            type="button"
            class="call-btn reject"
            :title="$t('Reject')"
            :aria-label="$t('Reject')"
            @click="reject"
          >
            <feather-icon
              icon="PhoneOffIcon"
              size="24"
            />
          </button>
          <button
            v-if="agoraCallType === 'incoming' && callStatus !== 'talking'"
            type="button"
            class="call-btn accept"
            :title="$t('Accept')"
            :aria-label="$t('Accept')"
            @click="accept"
          >
            <feather-icon
              icon="PhoneIcon"
              size="24"
            />
          </button>

          <button
            v-if="callStatus === 'talking' || (agoraCallType === 'outgoing' && callStatus === 'calling')"
            type="button"
            class="call-btn reject"
            :title="$t('End call')"
            :aria-label="$t('End call')"
            @click="leave"
          >
            <feather-icon
              icon="PhoneOffIcon"
              size="24"
            />
          </button>
        </div>
      </div>
      <div
        v-else
        class="not-expanded"
        role="button"
        tabindex="0"
        :aria-label="$t('Return to call')"
        @click="expanded = true"
        @keydown.enter.space.prevent="expanded = true"
      >
        <div
          v-if="callStatus === 'calling'"
          class="blink"
        />
        <div
          v-if="callStatus === 'talking'"
          class="static-blink"
        />
        <b-avatar
          v-if="agoraCallUser"
          size="64"
          :src="agoraCallUser.profile.image"
        />
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { getCallSounds, playCallSound, retryOnNextInteraction } from '@/helpers/callSounds'
import {
  BAvatar, BOverlay,
} from 'bootstrap-vue'
import AgoraRTC from 'agora-rtc-sdk-ng'
import useJwt from '@/auth/jwt/useJwt'
import { showNotify, throwDefaultError } from '@/helpers/helpers'
import { logEvent } from 'firebase/analytics'

const iconCallAccept = require('@/assets/images/icons/call-accept.png')
const iconCallReject = require('@/assets/images/icons/call-reject.png')

export default {
  name: 'AgoraCall',
  components: {
    BAvatar,
    BOverlay,
  },
  data() {
    return {
      callStatus: 'idle',
      channelId: null,
      expanded: true,
      heartBeatStatus: 1,
      iconCallAccept,
      iconCallReject,
      intervalForTime: null,
      intervalForHeartBeat: null,
      localeMuted: false,
      loading: false,
      message: '',
      remoteUser: null,
      remoteUserMuted: false,
      sendHeartBeat: true,
      // Uygulama genelinde ortak, ilk dokunuşta kilidi açılan zil sesleri (bkz. helpers/callSounds)
      ...getCallSounds(),
      stream: null,
      time: 0,
      timeCall: 0,
      timeCallInterval: null,
      timeIncomingCallTimeout: 120,
      timeOutgoingCallTimeout: 121,
      userAgoraInformation: null,
      // ---> Safari Audio Unlock için eklenen değişkenler <---
      audioContext: null,
      audioUnlocked: false,
      // ---> End Safari Audio Unlock <---
    }
  },
  async created() {
    // ---> Safari audio unlock durumu kontrolü <---
    const savedAudioUnlocked = localStorage.getItem('audioUnlocked')
    if (savedAudioUnlocked === '1') {
      this.audioUnlocked = true
      console.log('🔊 [Created] localStorage\'dan audioUnlocked durumu yüklendi:', this.audioUnlocked)
    } else {
      console.log('🔊 [Created] localStorage\'da audioUnlocked bulunamadı, varsayılan false')
    }
    // ---> End Safari audio unlock durumu kontrolü <---

    // ---> Ses dosyalarının durumu kontrolü <---
    console.log('🔊 [Created] Ses dosyaları durumu:')
    console.log('🔊 [Created] soundEndCall:', this.soundEndCall)
    console.log('🔊 [Created] soundIncomingCall:', this.soundIncomingCall)
    console.log('🔊 [Created] soundOutgoingCall:', this.soundOutgoingCall)
    // ---> End ses dosyaları durumu kontrolü <---

    this.message = `${this.$t('Connecting')}...`
    await this.startAgoraClient()
    // Bileşen bu sırada kapatıldıysa (ör. arama ekranı yeniden kuruldu) sahipsiz bir arama isteği gönderme
    // eslint-disable-next-line no-underscore-dangle
    if (this._isDestroyed) return
    this.message = this.$t('Connected')

    if (this.agoraClient && this.agoraCallUser) {
      if (this.agoraCallType === 'outgoing') {
        this.expanded = true
        this.message = `${this.$t('Calling')}...`
        await this.startCallingTime()
        await this.startCall()
      } else if (this.agoraCallType === 'incoming') {
        this.callStatus = 'calling'
        this.message = `${this.$t('Incoming call')}...`
        await this.startCallingTime()
        // Heartbeat göndermeyi başlat
        // this.heartBeatStatus = 6 // TODO : kapatılacak
        // this.startSendHeartBeat(this.callRequestInfo.agoraCall.callId, 5) // TODO : kapatılacak
      }
    }

    // Cancel call gelmesini dinle
    if (this.connection) {
      // Handler method olarak tutuluyor ki destroyed'da sökülebilsin;
      // aksi halde önceki sesli aramaların dinleyicileri birikip yeni gelen aramayı kapatıyor.
      this.connection.on('cancelCallRequest', this.onCancelCallRequest)
    }

    // ---> Safari için sayfa görünürlük değişikliği dinleyicisi <---
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    // ---> End Safari visibility change listener <---
    document.addEventListener('keydown', this.onCallKeydown)
  },
  destroyed() {
    // ---> Event listener temizleme <---
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    document.removeEventListener('keydown', this.onCallKeydown)
    this.setPageLocked(false)

    // Ses kilidi için açılan AudioContext'i kapat: önceden hiç kapatılmıyordu, Safari'de birkaç aramadan
    // sonra yeni ses motoru açılamıyor ve zil/ses bozuluyordu
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {})
    }
    // ---> End event listener temizleme <---

    // Interval leri temizle
    this.clearIntervals()

    this.stopAllSounds()

    if (this.connection) {
      this.connection.off('cancelCallRequest', this.onCancelCallRequest)
    }
  },
  watch: {
    // Arama kartı açıkken arka sayfa kaymasın ve sohbet widget'ı (tawk.to) kartın üstüne çıkmasın;
    // App.vue body'deki modal-open sınıfına bakarak widget'ı gizliyor. Küçültülünce sayfa serbest kalır.
    expanded: {
      immediate: true,
      handler(value) {
        this.setPageLocked(value)
        if (value) {
          this.$nextTick(() => {
            if (this.$refs.callCard) this.$refs.callCard.focus({ preventScroll: true })
          })
        }
      },
    },
    async callStatus(status) {
      this.stopAllSounds()

      if (this.callStatus === 'calling') {
        // ---> Safari audio unlock kontrolü <---
        if (!this.audioUnlocked) {
          // Kullanıcı etkileşimi yokken audioContext.resume() hiç dönmeyebiliyor; en fazla 1 sn bekle
          await Promise.race([
            this.unlockSafariAudio(),
            new Promise(resolve => setTimeout(resolve, 1000)),
          ])
        }
        // ---> End Safari audio unlock kontrolü <---

        // Unlock beklenirken durum değiştiyse artık zil çalma (AbortError'un sebebi buydu)
        if (this.callStatus !== status) return

        if (this.agoraCallType === 'outgoing' && this.soundOutgoingCall) {
          this.playRingtone(this.soundOutgoingCall, status)
        } else if (this.agoraCallType === 'incoming' && this.soundIncomingCall) {
          this.playRingtone(this.soundIncomingCall, status)
        }
      } else if (this.callStatus === 'ended') {
        // ---> Safari audio unlock kontrolü <---
        console.log('🔊 [Call Status] Call ended durumu, Safari audio unlock kontrolü yapılıyor...')
        if (!this.audioUnlocked) {
          console.log('🔊 [Call Status] Audio unlock gerekli, unlockSafariAudio çağrılıyor...')
          await this.unlockSafariAudio()
        } else {
          console.log('🔊 [Call Status] Audio zaten unlock, ses çalma işlemine devam...')
        }
        // ---> End Safari audio unlock kontrolü <---
        if (this.soundEndCall) {
          console.log('🔊 [Call Status] End call sesi çalınıyor...')
          console.log('🔊 [Call Status] soundEndCall objesi:', this.soundEndCall)
          console.log('🔊 [Call Status] soundEndCall.readyState:', this.soundEndCall.readyState)
          // ---> Audio unlock kontrolü <---
          if (this.audioUnlocked) {
            this.soundEndCall.loop = false
            playCallSound(this.soundEndCall).then(() => {
              console.log('🔊 [Call Status] End call sesi başarıyla çalındı')
            }).catch(error => {
              console.error('🔊 [Call Status] End call sesi çalınamadı:', error)
            })
            setTimeout(() => {
              this.stopAllSounds()
            }, 3000)
          } else {
            console.log('🔊 [Call Status] Audio unlock olmadığı için end call sesi çalınmıyor')
          }
          // ---> End audio unlock kontrolü <---
        }
      }
    },
    connecting() {},
    timeCall(val) {
      if (this.agoraCallType === 'incoming') {
        if (val >= this.timeIncomingCallTimeout) {
          this.reject()
        }
      } else if (this.agoraCallType === 'outgoing') {
        if (val >= this.timeOutgoingCallTimeout) {
          showNotify(this.$toast, this.$t('User didn\'t answer the call'), 'info')
          this.leave()
        }
      }
    },
  },
  computed: {
    agoraCallUser() {
      return this.$store.getters['agora/agoraCallUser']
    },
    agoraCallType() {
      return this.$store.getters['agora/agoraCallType']
    },
    agoraChannelParameters: {
      get() {
        return this.$store.getters['agora/agoraChannelParameters']
      },
      set(value) {
        this.$store.dispatch('agora/setAgoraChannelParameters', value)
      },
    },
    agoraClient() {
      return this.$store.getters['agora/agoraClient']
    },
    agoraOptions() {
      return this.$store.getters['agora/agoraOptions']
    },
    callRequestInfo() {
      return this.$store.getters['agora/callRequestInfo']
    },
    connection() {
      return this.$store.getters['socket/connection']
    },
    connecting() {
      return this.$store.getters['socket/connecting']
    },
  },
  methods: {
    setPageLocked(locked) {
      // Başka bir Bootstrap modal'ı açıksa onun modal-open sınıfını kaldırma
      const otherModalOpen = !!document.querySelector('.modal.show')
      document.body.classList.toggle('modal-open', locked || otherModalOpen)
    },
    // Esc: konuşma sürerken kartı küçültür (gelen arama çalarken küçültmez, kabul/reddet görünür kalsın)
    onCallKeydown(event) {
      if (event.key === 'Escape' && this.expanded && this.callStatus !== 'calling') {
        this.expanded = false
      }
    },
    currentCallId() {
      return this.agoraCallType === 'outgoing'
        ? this.userAgoraInformation?.callId
        : this.callRequestInfo?.agoraCall?.callId
    },
    onCancelCallRequest(data, ack) {
      if (typeof ack === 'function') ack('cancelCallRequestACK')

      // Başka bir aramaya ait iptal ise bu aramayı kapatma
      const currentCallId = this.currentCallId()
      if (data && data.callId && currentCallId && data.callId !== currentCallId) return

      this.leave(false, true)
    },
    async accept() {
      if (this.callRequestInfo) {
        await this.$store.dispatch('agora/setAgoraOptions', {
          channel: this.callRequestInfo.agoraCall.agoraChannelName,
          token: this.callRequestInfo.agoraCall.agoraToken,
          uid: this.callRequestInfo.agoraCall.agoraUid,
        })
        await this.join()

        // Heartbeat göndermeyi başlat
        // this.heartBeatStatus = 6 // TODO : açılacak
        // this.startSendHeartBeat(this.callRequestInfo.agoraCall.callId, 5) // TODO : açılacak
      }
    },
    async cancelCall() {
      if (this.agoraCallType === 'outgoing' && this.userAgoraInformation) {
        if (this.sendHeartBeat) {
          useJwt.appCallCancel({ callId: this.userAgoraInformation.callId })
        }
      }

      if (this.agoraCallType === 'incoming' && this.callRequestInfo) {
        useJwt.appCallCancel({ callId: this.callRequestInfo.agoraCall.callId })
      }
    },
    // İzin kontrolü metodu
    async checkMediaPermissions() {
      try {
        // Önce permission API ile kontrol et (Safari'de desteklenmeyebilir)
        // 'microphone' iznini tanımayan tarayıcılarda (bazı Safari sürümleri, Firefox) query TypeError fırlatıyor;
        // önceden bu hata "mikrofona erişilemiyor" sanılıp sesli arama iptal ediliyordu. Sadece açıkça
        // reddedilmişse durdur, sorgulanamıyorsa aşağıdaki getUserMedia ile gerçek izni kontrol et.
        let microphoneDenied = false
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const microphonePermission = await navigator.permissions.query({ name: 'microphone' })
            microphoneDenied = microphonePermission.state === 'denied'
          } catch (permissionQueryError) {
            microphoneDenied = false
          }
        }
        if (microphoneDenied) {
          const deniedError = new Error('Microphone permission denied')
          deniedError.name = 'NotAllowedError'
          throw deniedError
        }

        // Gerçek medya akışı iste (en güvenilir yöntem)
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        })

        // İzin alındı, stream'i kapat
        stream.getTracks().forEach(track => track.stop())
        return true
      } catch (error) {
        console.error('Media permission error:', error)

        // Hata tipine göre kullanıcıya mesaj göster
        if (error.name === 'NotAllowedError') {
          showNotify(this.$toast, this.$t('Microphone permission is required for voice calls. Please allow microphone access and try again.'), 'warning')
        } else if (error.name === 'NotFoundError') {
          showNotify(this.$toast, this.$t('No microphone found. Please connect a microphone and try again.'), 'error')
        } else {
          showNotify(this.$toast, this.$t('Unable to access microphone. Please check your settings.'), 'error')
        }

        return false
      }
    },
    clearIntervals() {
      this.stopCallingTime()

      if (this.intervalForTime) {
        clearInterval(this.intervalForTime)
        this.intervalForTime = null
      }
      if (this.intervalForHeartBeat) {
        clearInterval(this.intervalForHeartBeat)
        this.intervalForHeartBeat = null
      }
    },
    async closeAgoraCall(callId, sendCompletedHeartBeat = true, sendHeartBeatLater = false) {
      console.log('heartbeat sendHeartBeatLater', sendHeartBeatLater)
      console.log('heartbeat sendCompletedHeartBeat', sendCompletedHeartBeat)
      console.log('heartbeat callId', callId)
      if (sendCompletedHeartBeat && callId && this.callStatus !== 'calling' && this.callStatus !== 'idle') {
        console.log('heartbeat 11111111')
        // Backend e tamamlandı olarak heart beat gönder
        if (sendHeartBeatLater) {
          setTimeout(() => {
            this.heartBeat({ callId, status: 3 })
          }, 800)
        } else {
          this.heartBeat({ callId, status: 3 })
        }
      }

      this.callStatus = 'ended'

      // Firebase Analytics Event
      if (this.agoraCallType === 'outgoing' && window.firebaseAnalytics && this.agoraCallUser) {
        logEvent(window.firebaseAnalytics, 'web_end_call', {
          fromId: this.agoraCallUser.id,
          toId: this.agoraChannelParameters.remoteUid,
          duration: this.time,
        })
      }

      // Interval leri temizle
      this.clearIntervals()

      // Destroy the local audio track.
      if (this.agoraChannelParameters?.localAudioTrack) {
        await this.agoraChannelParameters.localAudioTrack.stop()
        await this.agoraChannelParameters.localAudioTrack.close()
        this.agoraChannelParameters.localAudioTrack = null
      }

      // Leave the channel
      await this.agoraClient.leave()
      this.message = this.$t('Closed')
      this.callStatus = 'idle'

      await this.$store.dispatch('agora/setAgoraCalling', false)
      await this.$store.dispatch('agora/setAgoraCallUser', null)
      await this.$store.dispatch('agora/setCallRequestInfo', null)
      await this.$store.dispatch('agora/setShowAgoraCall', false)
    },
    getDateForHuman(timeLeft) {
      if (timeLeft <= 0) {
        return '00:00'
      }

      let result = ''
      let totalSeconds = timeLeft

      const hours = Math.floor(totalSeconds / (60 * 60))
      totalSeconds -= hours * (60 * 60)

      const minutes = Math.floor(totalSeconds / (60))
      totalSeconds -= minutes * 60

      const seconds = Math.floor(totalSeconds % 60)

      result = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

      if (hours > 0) {
        result = `${hours.toString().padStart(2, '0')}:${result}`
      }

      return result
    },
    heartBeat(payload) {
      if (this.sendHeartBeat) {
        useJwt.heartBeat(payload)
          .then(() => {
            // console.log('heartBeat', response)
          })
          .catch(error => {
            const errorResponse = error.response
            // eslint-disable-next-line
            if (errorResponse && errorResponse.status === 400 && errorResponse.data?.header?.error?.errorCode == "514") {
              showNotify(this.$toast, this.$t('User didn\'t answer the call'), 'info')
              this.sendHeartBeat = false
              if (this.agoraCallType === 'outgoing') {
                this.leave()
              } else if (this.agoraCallType === 'incoming') {
                this.reject()
              }
              // eslint-disable-next-line
            } else if (errorResponse && errorResponse.status === 400 && errorResponse.data?.header?.error?.errorCode == "517") {
              showNotify(this.$toast, this.$t('Your credit is over'), 'info')
              // Kullanıcıyı market sayfasına yönlendir
              this.$router.push('/market')
              this.sendHeartBeat = false
              if (this.agoraCallType === 'outgoing') {
                this.leave()
              } else if (this.agoraCallType === 'incoming') {
                this.reject()
              }
            }
          })
      }
    },
    // eslint-disable-next-line consistent-return
    async join() {
      const hasPermission = await this.checkMediaPermissions()
      if (!hasPermission) {
        this.message = this.$t('Permission required')
        return false
      }
      // eslint-disable-next-line no-unused-vars
      const uid = this.agoraCallType === 'incoming' ? this.agoraOptions.uid.toString() : this.agoraOptions.uid
      // eslint-disable-next-line radix
      this.channelId = await this.agoraClient.join(this.agoraOptions.appId, this.agoraOptions.channel, this.agoraOptions.token, parseInt(this.agoraOptions.uid))
    },
    async leave(sendCancel = true, sendHeartBeatLater = false) {
      // Süreyi durdur
      clearInterval(this.intervalForTime)

      console.log('heartbeat sendHeartBeatLater', sendHeartBeatLater)

      // Store'da çağrı durumunu false yap
      await this.$store.dispatch('agora/setAgoraCalling', false)

      // Arama yapılıyor aşamasındaysa backend e arama bitirme isteği gönder
      console.log('hearbeat this.callStatus', this.callStatus)
      console.log('hearbeat sendCancel', sendCancel)
      if (this.callStatus === 'calling' && sendCancel) {
        await this.cancelCall()
      }

      console.log('heartbeat this.agoraCallType', this.agoraCallType)

      setTimeout(() => {
        if (this.agoraCallType === 'outgoing') {
          this.closeAgoraCall(this.userAgoraInformation.callId, true, sendHeartBeatLater)
        } else if (this.agoraCallType === 'incoming') {
          this.closeAgoraCall(this.callRequestInfo?.agoraCall?.callId)
        }
      }, 333)

      // Refresh the page for reuse
      // window.location.reload();
    },
    async localeMuteToggle() {
      if (this.agoraChannelParameters?.localAudioTrack) {
        if (this.localeMuted) {
          await this.agoraChannelParameters.localAudioTrack.setMuted(false)
          this.localeMuted = false
        } else {
          await this.agoraChannelParameters.localAudioTrack.setMuted(true)
          this.localeMuted = true
        }
      } else if (this.localeMuted) {
        try {
          this.agoraChannelParameters.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack()
          await this.agoraChannelParameters.localAudioTrack.setEnabled(true)
          // Publish the local audio track in the channel.
          await this.agoraClient.publish(this.agoraChannelParameters.localAudioTrack)

          this.localeMuted = false
        } catch (e) {
          this.localeMuted = true
        }
      }
    },
    async reject() {
      await this.cancelCall()
      if (this.callRequestInfo && this.callRequestInfo?.agoraCall?.callId) {
        await this.closeAgoraCall(this.callRequestInfo?.agoraCall?.callId, false)
      }
    },
    async startAgoraClient() {
      // Create an instance of the Agora Engine
      AgoraRTC.setLogLevel(3)
      await this.$store.dispatch('agora/setAgoraClient', AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' }))
      // this.agoraClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' })

      this.sendHeartBeat = true

      // Karşı taraf görüşmeye katıldı
      this.agoraClient.on('user-published', async (user, mediaType) => {
        // Kullanıcının verilerini değişkene ata
        this.remoteUser = user

        // Subscribe to the remote user when the SDK triggers the "user-published" event.
        await this.agoraClient.subscribe(user, mediaType)
        this.callStatus = 'talking'

        // Subscribe and play the remote audio track.
        if (mediaType === 'audio') {
          this.agoraChannelParameters.remoteUid = user.uid
          // Get the RemoteAudioTrack object from the AgoraRTCRemoteUser object.
          this.agoraChannelParameters.remoteAudioTrack = user.audioTrack
          // Play the remote audio track.
          this.agoraChannelParameters.remoteAudioTrack.play()

          this.remoteUserMuted = false
        }
      })

      // Listen for the "user-unpublished" event.
      this.agoraClient.on('user-unpublished', () => {
        this.remoteUserMuted = true
        this.agoraChannelParameters.remoteAudioTrack.stop()
      })

      // Listen for the "user-joined" event.
      this.agoraClient.on('user-joined', async () => {
        // Backend e başladı olarak heart beat gönder
        if (this.agoraCallType === 'outgoing') {
          this.heartBeat({ callId: this.userAgoraInformation.callId, status: 1 })
        }

        this.callStatus = 'talking'

        // Heart beat durumunu değiştir
        this.heartBeatStatus = 2
        if (this.agoraCallType === 'incoming') {
          this.startSendHeartBeat(this.callRequestInfo.agoraCall.callId, 5)
        } else if (this.agoraCallType === 'outgoing') {
          this.startSendHeartBeat(this.userAgoraInformation.callId, this.userAgoraInformation.heartbeatInterval)
        }

        // Süre işliyorsa durdur
        this.startTime()
      })

      // Listen for the "user-joined" event.
      this.agoraClient.on('user-left', async () => {
        // Store'da çağrı durumunu false yap
        this.$store.dispatch('agora/setAgoraCalling', false)

        console.log('heartbeat user-left')

        // Kullanıcı görüşmeden ayrıldı
        showNotify(this.$toast, this.$t('User has left the call'), 'info')

        console.log('heartbeat this.agoraCallType', this.agoraCallType)

        if (this.agoraCallType === 'outgoing') {
          await this.closeAgoraCall(this.userAgoraInformation.callId, true, true) // TODO: daha sonra sendCompletedHeartBeat false yapılacak
        } else if (this.agoraCallType === 'incoming') {
          await this.closeAgoraCall(null, false)
        }
      })

      // Bağlantı durum değişkliğini dinle
      this.agoraClient.on('connection-state-change', async e => {
        if (e === 'CONNECTED') {
          try {
            // Create a local audio track from the microphone audio.
            this.agoraChannelParameters.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack()
            await this.agoraChannelParameters.localAudioTrack.setEnabled(true)
            // Publish the local audio track in the channel.
            await this.agoraClient.publish(this.agoraChannelParameters.localAudioTrack)

            this.localeMuted = false
            // eslint-disable-next-line no-shadow
          } catch (e) {
            this.localeMuted = true
          }
        } else if (e === 'DISCONNECTED') {
          if (this.callStatus === 'talking') {
            this.leave()
          } else if (this.callStatus === 'calling') {
            if (this.agoraCallType === 'outgoing') {
              this.leave()
            } else if (this.agoraCallType === 'incoming') {
              this.reject()
            }
          }
        }
      })
    },
    startCall() {
      if (this.agoraClient && this.agoraCallUser) {
        // Store'da çağrı durumunu true yap
        this.$store.dispatch('agora/setAgoraCalling', true)

        const callDetails = this.$store.getters['app/callDetails']

        this.loading = true
        useJwt.appCall(this.agoraCallUser.id, callDetails)
          .then(async response => {
            if (response.data.body.callType === 'sippy') {
              showNotify(this.$toast, this.$t('Call information : agora not available, sippy is available'), 'info')
              await this.closeAgoraCall(null, false)
            } else {
              this.userAgoraInformation = response.data.body
              await this.$store.dispatch('agora/setAgoraOptions', {
                channel: this.userAgoraInformation.channelName,
                token: this.userAgoraInformation.token,
                uid: this.userAgoraInformation.uid,
              })
              const joined = await this.join()
              // Mikrofon izni yoksa join false döner; kanala girilmediği için aramayı iptal et
              // (yoksa karşı taraf boşuna çalmaya devam ediyordu)
              if (joined === false) {
                // leave() iptal isteğini yalnızca 'calling' durumunda backend'e gönderiyor
                this.callStatus = 'calling'
                await this.leave()
                return
              }
              if (this.agoraCallType === 'outgoing') {
                // Heartbeat göndermeyi başlat
                // this.heartBeatStatus = 6 // TODO : kapatılacak
                // this.startSendHeartBeat(this.userAgoraInformation.callId, this.userAgoraInformation.heartbeatInterval) // TODO : kapatılacak
              }
              this.callStatus = 'calling'
            }
          })
          .catch(error => {
            // Store'da çağrı durumunu false yap
            this.$store.dispatch('agora/setAgoraCalling', false)

            if (error.response?.data?.header?.error?.errorCode == '209') {
              // Kullanıcıyı market sayfasına yönlendir
              this.$router.push('/market')
            }

            this.message = this.$t('Call failed')
            throwDefaultError(this.$toast, error)

            this.closeAgoraCall(null, false)
          })
          // eslint-disable-next-line no-return-assign
          .finally(() => this.loading = false)
      }
    },
    async startCallingTime() {
      await this.stopCallingTime()

      this.timeCallInterval = setInterval(() => {
        // eslint-disable-next-line no-plusplus
        this.timeCall++
      }, 1000)
    },
    async stopCallingTime() {
      if (this.timeCallInterval) {
        clearInterval(this.timeCallInterval)
        this.timeCallInterval = null
        this.timeCall = 0
      }
    },
    startTime() {
      this.stopCallingTime()

      if (this.intervalForTime) {
        clearInterval(this.intervalForTime)
      }
      this.time = 0
      this.intervalForTime = setInterval(() => {
        // eslint-disable-next-line no-plusplus
        this.time++
        this.message = this.getDateForHuman(this.time)
      }, 1000)

      // Firebase Analytics Event
      if (this.agoraCallType === 'outgoing' && window.firebaseAnalytics && this.agoraCallUser) {
        logEvent(window.firebaseAnalytics, 'web_start_call', {
          fromId: this.agoraCallUser.id,
          toId: this.agoraChannelParameters.remoteUid,
        })
      }
    },
    // Zil sesini döngüde çalar (AgoraVideoCall.vue ile aynı mantık):
    // AbortError'da bir kez daha dener, NotAllowedError'da "sesi aç" uyarısı için audioUnlocked false kalır
    playRingtone(audio, status, retried = false) {
      if (!audio || this.callStatus !== status) return
      // eslint-disable-next-line no-param-reassign
      audio.loop = true
      playCallSound(audio)
        .then(() => {
          this.audioUnlocked = true
        })
        .catch(error => {
          if (error && error.name === 'AbortError' && !retried) {
            setTimeout(() => this.playRingtone(audio, status, true), 300)
          } else if (error && error.name === 'NotAllowedError') {
            this.audioUnlocked = false
            // Safari izin vermediyse zil sessizce kaybolmasın: kullanıcının ilk dokunuşunda tekrar dene
            retryOnNextInteraction(audio, () => this.callStatus === status)
          } else {
            console.warn('🔊 Zil sesi çalınamadı:', error)
          }
        })
    },
    stopAllSounds() {
      if (this.soundIncomingCall) {
        this.soundIncomingCall.pause()
        this.soundIncomingCall.currentTime = 0
      }
      if (this.soundOutgoingCall) {
        this.soundOutgoingCall.pause()
        this.soundOutgoingCall.currentTime = 0
      }
    },
    // ---> Safari Audio Unlock Metodu <---
    async unlockSafariAudio() {
      console.log('🔊 [Safari Audio] unlockSafariAudio başlatıldı')
      console.log('🔊 [Safari Audio] Mevcut audioUnlocked durumu:', this.audioUnlocked)
      try {
        // AudioContext oluştur
        console.log('🔊 [Safari Audio] AudioContext oluşturuluyor...')
        // Safari aynı anda açık ses motoru sayısını sınırlıyor; her çağrıda yenisini açmak yerine açık olanı kullan
        if (!this.audioContext || this.audioContext.state === 'closed') {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
        }
        console.log('🔊 [Safari Audio] AudioContext oluşturuldu:', this.audioContext)
        console.log('🔊 [Safari Audio] AudioContext state:', this.audioContext.state)
        if (this.audioContext.state === 'suspended') {
          console.log('🔊 [Safari Audio] AudioContext suspended, resume ediliyor...')
          await this.audioContext.resume()
          console.log('🔊 [Safari Audio] AudioContext resumed, yeni state:', this.audioContext.state)
        }

        // iOS/Safari için "unlock" hilesi: çok kısa ve sessiz bir ses üret
        console.log('🔊 [Safari Audio] iOS/Safari unlock hilesi başlatılıyor...')
        const oscillator = this.audioContext.createOscillator()
        const gainNode = this.audioContext.createGain()
        gainNode.gain.value = 0.0001 // Neredeyse sessiz
        oscillator.connect(gainNode).connect(this.audioContext.destination)
        oscillator.start()
        oscillator.stop(this.audioContext.currentTime + 0.05)
        console.log('🔊 [Safari Audio] iOS/Safari unlock hilesi tamamlandı')

        // Ses dosyalarını yeniden yükle
        console.log('🔊 [Safari Audio] Ses dosyaları yeniden yükleniyor...')
        console.log('🔊 [Safari Audio] soundEndCall:', this.soundEndCall)
        console.log('🔊 [Safari Audio] soundIncomingCall:', this.soundIncomingCall)
        console.log('🔊 [Safari Audio] soundOutgoingCall:', this.soundOutgoingCall)
        if (this.soundEndCall && this.soundEndCall.paused) {
          this.soundEndCall.load()
          console.log('🔊 [Safari Audio] soundEndCall.load() çağrıldı')
        }
        if (this.soundIncomingCall && this.soundIncomingCall.paused) {
          this.soundIncomingCall.load()
          console.log('🔊 [Safari Audio] soundIncomingCall.load() çağrıldı')
        }
        if (this.soundOutgoingCall && this.soundOutgoingCall.paused) {
          this.soundOutgoingCall.load()
          console.log('🔊 [Safari Audio] soundOutgoingCall.load() çağrıldı')
        }

        this.audioUnlocked = true
        localStorage.setItem('audioUnlocked', '1')
        console.log('🔊 [Safari Audio] Audio unlock başarılı! audioUnlocked:', this.audioUnlocked)
        // ---> Test ses çalma - kullanıcı etkileşimi sonrası <---
        try {
          console.log('🔊 [Safari Audio] Test ses çalınıyor...')
          // Çok kısa bir test sesi çal
          const testAudio = new Audio()
          testAudio.src = this.soundEndCall.src
          testAudio.volume = 0.1
          await testAudio.play()
          console.log('🔊 [Safari Audio] Test ses başarıyla çalındı!')
          // Test sesini hemen durdur
          testAudio.pause()
          testAudio.currentTime = 0
        } catch (testError) {
          console.error('🔊 [Safari Audio] Test ses çalınamadı:', testError)
        }
        // ---> End test ses çalma <---
        return true
      } catch (error) {
        console.error('🔊 [Safari Audio] Audio unlock başarısız:', error)
        console.error('🔊 [Safari Audio] Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
        })
        return false
      }
    },
    // ---> End Safari Audio Unlock <---
    // ---> Visibility Change Handler <---
    async handleVisibilityChange() {
      console.log('🔊 [Visibility] Sayfa görünürlük değişti, hidden:', document.hidden)
      console.log('🔊 [Visibility] AudioContext mevcut mu:', !!this.audioContext)
      if (!document.hidden && this.audioContext && this.audioContext.state === 'suspended') {
        console.log('🔊 [Visibility] AudioContext suspended, resume ediliyor...')
        try {
          await this.audioContext.resume()
          console.log('🔊 [Visibility] AudioContext resumed, yeni state:', this.audioContext.state)
        } catch (error) {
          console.error('🔊 [Visibility] AudioContext resume başarısız:', error)
        }
      } else {
        console.log('🔊 [Visibility] AudioContext resume gerekli değil veya mevcut değil')
      }
    },
    // ---> End Visibility Change Handler <---
    startSendHeartBeat(callId, heartbeatInterval) {
      if (this.intervalForHeartBeat) {
        clearInterval(this.intervalForHeartBeat)
        this.intervalForHeartBeat = null
      }

      // Backend heartbeat gönder
      this.intervalForHeartBeat = setInterval(() => {
        this.heartBeat({ callId, status: this.heartBeatStatus }) // status: Integer {1=started, 2=in_progress, 3=completed}
      }, heartbeatInterval * 1000)
    },
  },
}
</script>

<style lang="scss" scoped>
#agora-call {
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999998;
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
  }

  .expanded {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 24px;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    background-color: rgba(255, 255, 255, 0.95);
    border-radius: 20px;
    width: 520px;
    max-width: calc(100% - 48px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 9999999;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    animation: modalFadeIn 0.3s ease-out;
    flex-wrap: wrap;

    @keyframes modalFadeIn {
      from {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.9);
      }
      to {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
    }

    .call-information {
      display: block;
      div:first-of-type {
        display: flex;
        font-size: 18px;
        font-weight: 500;
        color: #2C3333;
        gap: 12px;

        div:first-of-type {
          display: flex;
          flex-direction: column;
          justify-content: center;
          color: #322E42 !important;
          font-family: 'Inter', sans-serif !important;

          span.username {
            font-size: 18px !important;
            font-weight: 600;
            width: 150px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          span.status {
            font-size: 13px !important;
            font-weight: 400;
            margin-top: 4px;
          }
        }
      }
    }

    .agora-call-buttons {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 16px;
      gap: 12px;
      width: 100%;

      * {
        cursor: pointer;
        transition: transform 0.2s ease;

        &:hover {
          transform: scale(1.1);
        }
      }
    }

    .window-actions {
      display: flex;
      align-items: start;
      position: absolute;
      right: 20px;
      top: 20px;

      .minus {
        font-size: 22px;
        line-height: 4px;
        cursor: pointer;
        color: #666;
        -webkit-transform: scale(1.8, 1.2);
        -moz-transform: scale(1.8, 1.2);
        -ms-transform: scale(1.8, 1.2);
        -o-transform: scale(1.8, 1.2);
        transform: scale(1.8,1.2);
        transition: color 0.2s ease;

        &:hover {
          color: #333;
        }
      }
    }

    @media screen and (max-width: 640px) {
      width: calc(100% - 32px);
      padding: 20px;
    }
  }

  .not-expanded {
    position: fixed;
    left: 12px;
    bottom: 24px;
    width: 84px;
    height: 84px;
    border-radius: 50%;
    overflow: clip;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 9999;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }

    .static-blink {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      position: absolute;
      left: 2px;
      top: 2px;
      outline: 2px solid #a9ead9;
    }

    .blink {
      width: 84px;
      height: 84px;
      border-radius: 50%;
      position: absolute;
      left: 0;
      top: 0;
      outline: 2px solid #a9ead9;
      animation: blink-animation 1s steps(20, start) infinite;
      -webkit-animation: blink-animation 1s steps(20, start) infinite;
    }
    @keyframes blink-animation {
      to {
        width: 64px;
        height: 64px;
        left: 10px;
        top: 10px;
      }
    }
    @-webkit-keyframes blink-animation {
      to {
        width: 84px;
        height: 84px;
        left: 0;
        top: 0;
      }
    }
  }
}

.safari-audio-unlock-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999998;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: #fff;
  font-size: 24px;
  font-weight: bold;
  text-align: center;
  padding: 20px;

  .safari-audio-unlock-content {
    background-color: rgba(0, 0, 0, 0.7);
    padding: 30px;
    border-radius: 15px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }

  .safari-audio-unlock-icon {
    font-size: 60px;
  }

  .safari-audio-unlock-text {
    font-size: 18px;
    color: #fff;
    margin-bottom: 20px;
  }

  .safari-audio-unlock-button {
    background-color: #007bff;
    color: #fff;
    padding: 10px 20px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-size: 16px;
    font-weight: bold;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: #0056b3;
    }
  }
}

/* =========================================================
   Sesli arama — yeni koyu UI (sadece görünüm)
   ========================================================= */
#agora-call {
  .modal-overlay {
    background-color: rgba(6, 3, 20, 0.7);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }

  .expanded {
    width: 380px;
    max-width: calc(100% - 32px);
    padding: 34px 24px 28px;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    background: linear-gradient(160deg, #2b1a63 0%, #1d1247 55%, #3a1356 100%);
    border: 1px solid rgba(234, 3, 178, 0.35);
    border-radius: 26px;
    box-shadow: 0 24px 60px rgba(4, 1, 20, 0.7), 0 0 30px rgba(234, 3, 178, 0.2);
    color: #fff;

    .call-glow {
      position: absolute;
      top: -30%;
      left: 50%;
      transform: translateX(-50%);
      width: 120%;
      height: 70%;
      background: radial-gradient(ellipse at center, rgba(234, 3, 178, 0.35), transparent 65%);
      pointer-events: none;
    }

    .call-information {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      width: 100%;

      .call-avatar-wrap {
        position: relative;
        width: 120px;
        height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
        font-size: inherit;
        gap: 0;
      }

      .call-avatar {
        position: relative;
        z-index: 2;
        border: 4px solid rgba(255, 255, 255, 0.15);
        box-shadow: 0 0 0 3px #ea03b2, 0 0 30px rgba(234, 3, 178, 0.55);
      }

      .call-ring {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        border: 2px solid rgba(234, 3, 178, 0.6);
        animation: callRing 2s ease-out infinite;

        &.ring-2 { animation-delay: 1s; }
      }

      span.username {
        color: #fff !important;
        font-family: inherit !important;
        font-size: 22px !important;
        font-weight: 800;
        width: auto;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      span.status {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 6px;
        color: rgba(236, 232, 255, 0.8) !important;
        font-size: 14px !important;
        font-weight: 500;
      }

      .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #f59e0b;
        box-shadow: 0 0 8px #f59e0b;

        &.talking { background: #22c55e; box-shadow: 0 0 8px #22c55e; }
        &.ended { background: #ef4444; box-shadow: 0 0 8px #ef4444; }
      }
    }

    .agora-call-buttons {
      position: relative;
      z-index: 1;
      margin-top: 26px;
      gap: 18px;
      flex-wrap: wrap;

      * { transition: none; &:hover { transform: none; } }

      .call-btn-wrap { display: inline-flex; }

      .call-btn {
        width: 62px;
        height: 62px;
        border-radius: 50%;
        border: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        cursor: pointer;
        transition: transform .15s ease, filter .15s ease !important;

        &:hover { transform: scale(1.08) !important; filter: brightness(1.1); }

        &.accept {
          background: linear-gradient(135deg, #10b981, #22c55e);
          box-shadow: 0 8px 22px rgba(16, 185, 129, 0.5);
          animation: callPulse 1.4s ease-in-out infinite;
        }

        &.reject {
          background: linear-gradient(135deg, #ef4444, #e11d48);
          box-shadow: 0 8px 22px rgba(239, 68, 68, 0.5);
        }

        &.neutral {
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.22);
          backdrop-filter: blur(6px);

          &.muted {
            background: rgba(239, 68, 68, 0.22);
            border-color: rgba(239, 68, 68, 0.55);
            color: #fca5a5;
          }
        }
      }
    }

    .window-actions {
      z-index: 2;
      top: 14px;
      right: 14px;

      .minus {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        border: 1px solid rgba(170, 140, 255, 0.35);
        background: rgba(14, 10, 40, 0.6);
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transform: none;
        line-height: 1;

        &:hover { background: rgba(139, 92, 246, 0.35); color: #fff; }
      }
    }
  }

  .not-expanded {
    left: 14px;
    bottom: calc(84px + env(safe-area-inset-bottom, 0px));
    box-shadow: 0 0 0 3px #ea03b2, 0 8px 24px rgba(234, 3, 178, 0.45);

    @media (min-width: 769px) { bottom: 24px; }

    .blink,
    .static-blink { outline-color: #ea03b2; }
  }
}

@keyframes callRing {
  0% { transform: scale(1); opacity: 0.9; }
  100% { transform: scale(1.55); opacity: 0; }
}

@keyframes callPulse {
  0%, 100% { box-shadow: 0 8px 22px rgba(16, 185, 129, 0.5), 0 0 0 0 rgba(34, 197, 94, 0.55); }
  50% { box-shadow: 0 8px 22px rgba(16, 185, 129, 0.5), 0 0 0 12px rgba(34, 197, 94, 0); }
}

// Mobil: kısa/yatay ekranlarda sesli arama kartının üstü-altı kesilmesin, sığmazsa kaydırılsın
#agora-call .expanded {
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  overflow-y: auto !important;
  touch-action: manipulation;
}

// Dokunmatik ekranda butona dokunduktan sonra büyüme efekti takılı kalmasın
@media (hover: none) {
  #agora-call .expanded .agora-call-buttons .call-btn:hover {
    transform: none !important;
    filter: none;
  }
}

#agora-call {
  .expanded {
    // Kart içindeki kaydırma arka sayfaya geçmesin
    overscroll-behavior: contain;

    &:focus { outline: none; }

    // Süre her saniye değişirken yazı genişliği oynamasın
    .call-information span.status { font-variant-numeric: tabular-nums; }

    // Küçültme butonu görünümü aynı, dokunma alanı 46px
    .window-actions .minus { position: relative; }

    .window-actions .minus::after {
      content: '';
      position: absolute;
      top: -6px;
      right: -6px;
      bottom: -6px;
      left: -6px;
    }
  }

  // Mobilde küçük arama balonu alttaki kampanya şeridinin üstüne binmesin
  @media (max-width: 768px) {
    .not-expanded { bottom: calc(146px + env(safe-area-inset-bottom, 0px)); }
  }

  // Dar telefonlarda kart iç boşluğu ve isim biraz küçülür, içerik sığar
  @media (max-width: 400px) {
    .expanded {
      padding: 30px 18px 24px;

      .call-information span.username { font-size: 20px !important; }
    }
  }

  @media (hover: none) {
    .not-expanded:hover { transform: none !important; }
  }
}

// Nirva düzeni (renkler Boho'nun): kişi bilgisi ortada, butonlar kartın altında üst çizgili ayrı bir
// panelde. 640px ve üstü: ortalanmış kart (en fazla 520px, yükseklik min(78dvh, 540px));
// 640px altı: tam ekran.
#agora-call .expanded {
  padding: 0;
  justify-content: flex-start;

  .call-information {
    flex: 1 1 auto;
    justify-content: center;
    padding: 40px 24px 24px;
  }

  .agora-call-buttons {
    position: relative;
    z-index: 1;
    width: 100%;
    margin-top: 0;
    padding: 14px 16px 16px;
    justify-content: center;
    border-top: 1px solid rgba(234, 3, 178, 0.3);
    background: linear-gradient(160deg, #2b1a63 0%, #1d1247 55%, #3a1356 100%);
  }
}

@media (min-width: 640px) {
  #agora-call .expanded {
    width: min(520px, calc(100% - 32px));
    max-width: none;
    height: min(78vh, 540px);
    height: min(78dvh, 540px);
  }
}

@media (max-width: 639px) {
  #agora-call .expanded {
    top: 0;
    left: 0;
    width: 100%;
    max-width: none;
    height: 100vh;
    height: 100dvh;
    max-height: none;
    transform: none;
    border: 0;
    border-radius: 0;
    animation: none;

    .call-information {
      padding-top: calc(40px + env(safe-area-inset-top, 0px));
    }

    .agora-call-buttons {
      padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    }
  }
}
</style>

<style lang="scss">
// Arama kartı (z-index 9999999) açıkken "izin gerekli" gibi uyarılar kartın altında kalıp görünmüyordu
.Vue-Toastification__container {
  z-index: 10000000 !important;
}
</style>
